import { Component, Input, OnDestroy, OnInit, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DTypeForm, DTypeFormArray, DTypeFormTableWithVariableRowsField } from '@grupo-constant/next-dtype/form';
import { CODEBARSCANNERWITHSELECT_URI, DTypeStatus, DTypeViewableCol, SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMURI_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { DTypeService } from '../../dtype.service';
import { LocaleString } from '../../../translate/localeString.model';
import { LocalizePipe } from '../../../pipes/localize.pipe';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { DialogsService } from '../../../../dialogs/dialogs.service';

@Component({
    selector: 'tablewithvariablerows-field-component',
    templateUrl: './tablewithvariablerows-field.component.html',
    styles: [
        /* language=SCSS */
        `
            .component-grid {
                grid-template-columns: 70px 70px auto;

                @screen sm {
                    grid-template-columns: 70px 70px auto;
                }

                @screen md {
                    grid-template-columns: 70px 70px auto;
                }

                @screen lg {
                    grid-template-columns: 70px 70px auto;
                }
                @screen xl {
                    grid-template-columns: 70px 70px auto;
                }
                @screen 2xl {
                    grid-template-columns: 70px 70px auto;
                }
                @screen 3xl {
                    grid-template-columns: 70px 70px auto;
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeTableWithVariableRowsFieldComponent implements OnInit, OnDestroy, DTypeFormFieldComponent {
    @ViewChildren(MatExpansionPanel) panels: MatExpansionPanel;

    @Input() field: DTypeFormTableWithVariableRowsField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    options: [] = [];
    hiddenOptions: any[] = [];

    panelOpenState = false;
    panel: number;
    oldPanel: number;
    canAddRow: boolean = true;
    rowsSave: number[] = [];
    saving: boolean = false;

    tableHeader: LocaleString[] = [];
    tableData: unknown[][] = [];

    editView: boolean = true;
    repeatRows: boolean = false;

    private _unsubscribeAll = new Subject<void>();


    constructor(private dtypeService: DTypeService,
        private localize: LocalizePipe,
        private fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private snackBar: MatSnackBar,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,) { }

    async ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }

        if (this.field.tableWithVariableRows.dType['uniqueIndex'].length === 0) {
            this.warningDialog(this.transloco.translate('mustAddUniquesIndex'));
        }

        if (this.field.embeddedForms.length === 0) {
            let newEmbeddedForm: DTypeForm;
            if (this.field.tableWithVariableRows.dType) { // El campo ya tiene el DType (más práctico)
                newEmbeddedForm = this.dtypeService.getDtypeFormFromDtype(this.field.tableWithVariableRows.dType, { isUpdate: this.isUpdate });
            } else {
                newEmbeddedForm = await this.dtypeService.getDtypeFormByUri({
                    uri: this.field.uri, status: DTypeStatus.DESIGN, bindableOptions: true
                }, {}, {
                    readonly: this.field.readonly,
                    isUpdate: this.isUpdate
                });
            }
            if (newEmbeddedForm) {
                this.field.embeddedForms.push(newEmbeddedForm);
                (this.field.control as DTypeFormArray).push(newEmbeddedForm.control);
                if (this.field.tableWithVariableRows.dType['uniqueIndex'].length > 0) {
                    newEmbeddedForm.control.valueChanges.subscribe((value) => {
                        if (value) {
                            this.rowsSave = this.rowsSave.filter(r => r !== 0);
                        }
                    });
                }
            }
            this.panel = 0;
            this.oldPanel = 0;
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    async addItem() {
        this.oldPanel = this.panel;
        if (!this.rowsSave.some(r => r === this.panel)) {
            this.saveItem(this.field.embeddedForms[this.panel], this.panel);
        }
        if (this.canAddRow) {
            let newEmbeddedForm: DTypeForm;
            if (this.field.tableWithVariableRows.dType) { // El campo ya tiene el DType (más práctico)
                newEmbeddedForm = this.dtypeService.getDtypeFormFromDtype(this.field.tableWithVariableRows.dType, { isUpdate: this.isUpdate });
            } else {
                newEmbeddedForm = await this.dtypeService.getDtypeFormByUri({
                    uri: this.field.uri, status: DTypeStatus.DESIGN, bindableOptions: true
                }, {}, {
                    readonly: this.field.readonly,
                    isUpdate: this.isUpdate
                });
            }

            if (newEmbeddedForm) {
                this.field.embeddedForms.push(newEmbeddedForm);
                (this.field.control as DTypeFormArray).push(newEmbeddedForm.control);
                if (this.field.tableWithVariableRows.dType['uniqueIndex'].length > 0) {
                    newEmbeddedForm.control.valueChanges.subscribe((value) => {
                        if (value) {
                            const index = this.panel;
                            if (index > -1) {
                                this.rowsSave = this.rowsSave.filter(r => r !== index);
                            }
                        }
                    });
                }
            }
            this.panels['_results'][this.panel]._expanded = false;
            this.panel = this.panels['_results'].length;
            this.canAddRow = false;
        }
    }

    async saveItem(form: DTypeForm, index: number) {
        this.saving = true;
        let saved: boolean = false;
        let repeated: boolean = false;
        let indexRepeated: number;
        form.control.markAllAsTouched();
        if (form.control.valid) {
            if (this.field.embeddedForms.length > 1 && this.field.tableWithVariableRows.dType['uniqueIndex'].length > 0 && !this.repeatRows) {
                const oidForm = this.createOidForm(form);
                for (const f of this.field.embeddedForms.filter(eform => eform !== form)) {
                    const ind = this.field.embeddedForms.findIndex(_form => _form === f);
                    const oidAlreadyCreated = this.createOidForm(f);
                    if (oidForm === oidAlreadyCreated) {
                        const replace = await this.openConfirmDialog(this.transloco.translate('rowAlreadyExist'), this.transloco.translate('replacePreviousRow'),
                            this.transloco.translate('editExistingRow'));
                        if (replace) {
                            for (const control of Object.values(form.control.controls)) {
                                control.setValue(control.value ? control.value : null);
                            }
                            f.control.setValue(form.control.value);
                        }
                        this.panel = ind;
                        this.panels['_results'][this.panel]._expanded = false;
                        this.panels['_results'][ind]._expanded = true;
                        this.removeItem(index, 'save');
                        indexRepeated = ind;
                        repeated = true;
                        saved = true;
                        break;
                    } else {
                        saved = true;
                    }
                }
            } else {
                saved = true;
            }
        } else {
            this.panels['_results'][index]._expanded = true;
            this.panels['_results'][this.panel]._expanded = false;
            this.panel = index;
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('errorsInForm')
            });
        }

        if (saved) {
            this.canAddRow = true;
            const ind = repeated ? indexRepeated : index;
            if (!this.rowsSave.some(r => r === ind)) {
                this.rowsSave.push(index);
            }
            this.snackBar.open(this.transloco.translate('rowSaved'), this.transloco.translate('accept'), { duration: 3000 });
        } else {
            this.canAddRow = false;
        }
        this.saving = false;
    }

    async setPanel(index: number) {
        this.panel = index;
    }

    createOidForm(form: DTypeForm) {
        let oidForm: string = '';
        for (const index of this.field.tableWithVariableRows.dType['uniqueIndex']) {
            oidForm = oidForm + form.control.controls[index].value;
        }
        return oidForm;
    }

    chooseOption(form: DTypeForm) {
        let value: string = '';
        for (const index of this.field.tableWithVariableRows.dType['uniqueIndex']) {
            const valueOid = form.control.controls[index].value;
            const selectOptions = this.field['dTypeCol']['dType'].column.find(c => c.name === index).option;
            if (this.localize.transform(selectOptions.find(o => o.code === valueOid)?.value)) {
                if (value) {
                    value = value + ' - ' + this.localize.transform(selectOptions.find(o => o.code === valueOid)?.value);
                } else {
                    value = this.localize.transform(selectOptions.find(o => o.code === valueOid)?.value);
                }
            }
        }
        return value;
    }

    async removeItem(index: number, str: string = undefined) {
        if (!str) {
            const confirm = await this.openConfirmDialog(this.transloco.translate('removeExistingRow'), this.transloco.translate('accept'),
                this.transloco.translate('cancel'));
            if (confirm) {
                this.field.embeddedForms.splice(index, 1);
                this.rowsSave.splice(index, 1);
                this.panels['_results'].splice(index, 1);
                (this.field.control as DTypeFormArray).removeAt(index);
                this.panel = this.panels['_results'].length - 1;
            }
        } else {
            this.field.embeddedForms.splice(index, 1);
            this.rowsSave.splice(index, 1);
            (this.field.control as DTypeFormArray).removeAt(index);
        }
        this.canAddRow = true;
    }


    async closePanel(index: number) {
        const saved = this.rowsSave.some(r => r === index);
        if (!saved && !this.saving && !this.repeatRows) {
            this.panels['_results'][index]._expanded = true;
            await this.saveItem(this.field.embeddedForms[index], index);
            if (!this.rowsSave.some(r => r === index)) {
                for (const row of this.rowsSave) {
                    if (row !== index) {
                        this.panels['_results'][row]._expanded = false;
                    }
                }
                if (this.panel === index) {
                    this.panels['_results'][index]._expanded = true;
                }
            } else {
                this.panels['_results'][index]._expanded = false;
            }
        }
        this.oldPanel = index;
    }

    createTableView() {
        this.tableData = [];
        this.tableHeader = [];
        this.editView = !this.editView;
        for (const field of Object.values(this.field.embeddedForms[0].fields)) {
            this.tableHeader.push(field.label);
        }
        for (const form of this.field.embeddedForms) {
            const values: unknown[] = [];
            let value;
            for (const column of form.dType.column) {
                const index = form.dType.column.findIndex(c => c === column);
                if (index > -1) {
                    if (column.type === SELECTWITHGIVENOPTIONS_COLUMN_URI || column.type === SELECTFROMURI_COLUMN_URI || column.type === SELECTFROMMASTERDATA_COLUMN_URI
                        || column.type === CODEBARSCANNERWITHSELECT_URI) {
                        value = this.localize.transform(column['option'].find(o => o.code === Object.values(form.control.value)[index])?.value);
                        values.push(value);
                    } else {
                        value = Object.values(form.control.value)[index];
                        values.push(value);
                    }
                }
            }
            this.tableData.push(values);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private warningDialog(message: string) {
        const dialog = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('accept')
                },
                cancel: {
                    label: this.transloco.translate('cancel'),
                    show: false
                }
            }
        });
    }

    private openConfirmDialog(message: string, confirmButton: string, cancelButton: string) {
        const dialogRef = this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate(message),
            actions: {
                confirm: {
                    label: this.transloco.translate(confirmButton)
                },
                cancel: {
                    label: this.transloco.translate(cancelButton)
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

}


